import { Icon } from "@iconify/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { getTooltipClassName } from "../../../../utils";
import type { ChatSessionSummary } from "../../api/getChatSessions";
import { setChatSessionFavorite } from "../../api/setFavorite";
import { unsetChatSessionFavorite } from "../../api/unsetFavorite";

interface SessionItemProps {
  session: ChatSessionSummary;
  isDarkMode: boolean;
  selectedSessionId: string | null;
  setSelectedSessionId: (id: string) => void;
  username: string;
}

function SessionItem({
  session,
  isDarkMode,
  selectedSessionId,
  setSelectedSessionId,
  username,
}: SessionItemProps) {
  const queryClient = useQueryClient();

  // Mutation for setting favorite
  const setFavoriteMutation = useMutation({
    mutationFn: () => setChatSessionFavorite(session.uid, { is_favorite: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatSessions", username] });
      toast.success("Chat session marked as favorite");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail || error?.message || "Failed to set favorite";
      toast.error(errorMessage);
    },
  });

  // Mutation for unsetting favorite
  const unsetFavoriteMutation = useMutation({
    mutationFn: () => unsetChatSessionFavorite(session.uid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatSessions", username] });
      toast.success("Chat session unmarked as favorite");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail || error?.message || "Failed to unset favorite";
      toast.error(errorMessage);
    },
  });

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (session.is_favorite) {
      unsetFavoriteMutation.mutate();
    } else {
      setFavoriteMutation.mutate();
    }
  };

  return (
    <div className="grid grid-cols-[1fr_auto] items-start gap-2 mb-2! min-w-0">
      <button
        type="button"
        onClick={() => setSelectedSessionId(session.uid)}
        className={`w-full p-2 md:p-3 rounded-lg! text-left transition-colors min-w-0 ${
          selectedSessionId === session.uid
            ? isDarkMode
              ? "bg-gray-700!"
              : "bg-blue-100!"
            : isDarkMode
              ? "hover:bg-gray-700!"
              : "hover:bg-gray-200!"
        }`}
      >
        <div className="grid grid-cols-[1fr_auto] items-start gap-2 min-w-0">
          <div className="min-w-0 overflow-hidden">
            <div className="grid grid-cols-[1fr_auto] items-center gap-2 mb-1">
              <h6
                className={`text-sm! font-medium truncate ${
                  selectedSessionId === session.uid
                    ? isDarkMode
                      ? "text-gray-100!"
                      : "text-gray-900!"
                    : isDarkMode
                      ? "text-gray-200!"
                      : "text-gray-800!"
                }`}
              >
                {session.name || "Chat Session"}
              </h6>
              {session.submitted && (
                <span className={`submitted-icon-${session.uid}`}>
                  <Icon
                    icon="mdi:check-circle"
                    className={`text-sm ${isDarkMode ? "text-green-400" : "text-green-600"}`}
                  />
                </span>
              )}
            </div>
            <span className={`text-xs text-gray-400!`}>
              {(() => {
                const dateTime = DateTime.fromISO(session.created);
                return dateTime.toLocaleString(DateTime.DATETIME_FULL);
              })()}
            </span>
          </div>
        </div>
      </button>
      <button
        type="button"
        onClick={handleFavoriteClick}
        className={`p-2 rounded-lg! transition-colors self-start favorite-btn-${session.uid} ${
          session.is_favorite
            ? isDarkMode
              ? "text-yellow-400 hover:text-yellow-300"
              : "text-yellow-500 hover:text-yellow-600"
            : isDarkMode
              ? "text-gray-500 hover:text-gray-400"
              : "text-gray-400 hover:text-gray-500"
        }`}
        disabled={setFavoriteMutation.isLoading || unsetFavoriteMutation.isLoading}
      >
        <Icon icon={session.is_favorite ? "mdi:star" : "mdi:star-outline"} className="text-lg" />
      </button>
      <ReactTooltip
        anchorSelect={`.favorite-btn-${session.uid}`}
        place="top"
        content={session.is_favorite ? "Remove from favorites" : "Add to favorites"}
        className={getTooltipClassName(isDarkMode)}
      />
      {session.submitted && (
        <ReactTooltip
          anchorSelect={`.submitted-icon-${session.uid}`}
          place="top"
          content="Submitted"
          className={getTooltipClassName(isDarkMode)}
        />
      )}
    </div>
  );
}

export { SessionItem };
